// Branches page logic shared by the current page (v1, BranchOverview) and
// the redesigned one (v2, BranchesV2), so the two cannot drift apart: how the
// selection aggregates, the detail cards' formulas (decision D16 keeps today's),
// the AI value by location view, and the v2-only comparison flags (D15) and
// inventory links (D14).

import { VERDICT_ORDER } from '../components/BranchOverview/branchOverviewStyles';
import {
    GAP_THRESHOLDS, gapDirection, summarizeByBranch, summarizeLotValue,
} from './lotValue';
import { ALL_BRANCHES_PARAM } from './inventoryView';
import { sellSpeedCategory } from './sellSpeedCategory';

const UNKNOWN_BRANCH = 'Unknown';

/** Same fallback as summarizeByBranch, so every grouping agrees. */
export const branchOf = (unit) => unit?.BranchName || UNKNOWN_BRANCH;

/** Inventory units by BranchName ("Unknown" when missing). */
export const groupUnitsByBranch = (units) => {
    const grouped = {};
    (units || []).forEach((unit) => {
        const name = branchOf(unit);
        if (!grouped[name]) grouped[name] = [];
        grouped[name].push(unit);
    });
    return grouped;
};

/**
 * The selected /combined branches as one: a single branch passes through;
 * several are summed, each average weighted by its own denominator (days in
 * stock by units, `total`; listing price and days listed by listings,
 * `total_listings`), the shares recomputed (risk over scored units). Null
 * when nothing is selected.
 */
export const aggregateBranches = (branches, selectedNames) => {
    if (!branches?.length) return null;
    const selected = branches.filter((b) => selectedNames.has(b.branch_name));
    if (selected.length === 0) return null;
    if (selected.length === 1) {
        return { ...selected[0], displayTitle: selected[0].branch_name };
    }

    const sum = (key) => selected.reduce((acc, b) => acc + (b[key] || 0), 0);
    const weightedAvg = (key, weightKey = 'total') => {
        let num = 0;
        let den = 0;
        selected.forEach((b) => {
            if (b[key] != null && b[weightKey]) {
                num += b[key] * b[weightKey];
                den += b[weightKey];
            }
        });
        return den > 0 ? num / den : null;
    };

    const total = sum('total');
    const critical = sum('critical');
    const hot = sum('hot');
    const warm = sum('warm');
    const riskHigh = sum('risk_high');
    const riskScored = riskHigh + sum('risk_medium') + sum('risk_low');
    const isAll = selected.length === branches.length;
    const title = isAll ? `All ${branches.length} branches` : `${selected.length} branches selected`;

    return {
        branch_name: title,
        displayTitle: title,
        total,
        total_value: sum('total_value') || null,
        total_listings: sum('total_listings') || null,
        fresh: sum('fresh'),
        normal: sum('normal'),
        slow: sum('slow'),
        critical,
        hot,
        warm,
        cold: sum('cold'),
        no_interest: sum('no_interest'),
        total_quoted: sum('total_quoted'),
        avg_price: weightedAvg('avg_price', 'total_listings'),
        avg_days_listed: weightedAvg('avg_days_listed', 'total_listings'),
        max_days_listed: Math.max(0, ...selected.map((b) => b.max_days_listed || 0)) || null,
        avg_days_in_stock: weightedAvg('avg_days_in_stock') || 0,
        critical_pct: total > 0 ? (critical / total) * 100 : 0,
        active_pct: total > 0 ? ((hot + warm) / total) * 100 : 0,
        risk_high: riskHigh,
        risk_medium: sum('risk_medium'),
        risk_low: sum('risk_low'),
        risk_unscored: sum('risk_unscored'),
        pct_high_risk: riskScored > 0 ? (riskHigh / riskScored) * 100 : 0,
    };
};

/** "Minot" / "3 branches selected" / "All 13 branches" (today's wording). */
export const selectionTitle = (names, total) => {
    const n = names.length;
    if (n === 0) return 'No branches selected';
    if (n === 1) return names[0];
    if (total > 0 && n === total) return `All ${total} branches`;
    return `${n} branches selected`;
};

// ── Detail cards (D16: today's formulas) ─────────────────────────────────

/**
 * Every category (no cut), by count; "critical" = units over the
 * dealership's critical days (aging.critical_days, 90 by default).
 */
export const categoryBreakdown = (units, criticalDays = 90) => {
    if (!units?.length) return [];
    const map = {};
    units.forEach((u) => {
        const cat = u.CategoryDisplay || u.CATEGORY || UNKNOWN_BRANCH;
        if (!map[cat]) map[cat] = { count: 0, critical: 0, totalDays: 0 };
        map[cat].count += 1;
        if ((u.DaysInStock || 0) > criticalDays) map[cat].critical += 1;
        map[cat].totalDays += (u.DaysInStock || 0);
    });
    return Object.entries(map)
        .map(([name, d]) => ({
            name,
            count: d.count,
            critical: d.critical,
            avgDays: d.count > 0 ? Math.round(d.totalDays / d.count) : 0,
            pct: Math.round((d.count / units.length) * 100),
        }))
        .sort((a, b) => b.count - a.count);
};

/** Units per AnomalyVerdict, in VERDICT_ORDER, zeros left out. */
export const verdictBreakdown = (units) => {
    if (!units?.length) return [];
    const map = {};
    units.forEach((u) => { if (u.AnomalyVerdict) map[u.AnomalyVerdict] = (map[u.AnomalyVerdict] || 0) + 1; });
    return VERDICT_ORDER.filter((v) => map[v] > 0).map((v) => ({ verdict: v, count: map[v] }));
};

/**
 * Price alignment vs AI: the MEAN of per-unit asking / AI ratios over units
 * with both prices; null with fewer than 3 such units. Each unit is counted
 * overpriced / aligned / below on the inventory's gap scale (the
 * dealership's thresholds via gapDirection; by default over +5%, under −5%).
 */
export const priceAlignment = (units, thresholds) => {
    const pairs = (units || []).filter((u) => (u.PredictedPrice || 0) > 0 && (u.Best_Sell_Price || 0) > 0);
    if (pairs.length < 3) return null;
    const ratios = pairs.map((u) => (u.Best_Sell_Price / u.PredictedPrice) * 100);
    const avg = ratios.reduce((a, b) => a + b, 0) / ratios.length;
    const dirs = ratios.map((r) => gapDirection(r / 100 - 1, thresholds));
    return {
        avg: Math.round(avg),
        overpriced: dirs.filter((d) => d === 'above' || d === 'wellAbove').length,
        aligned: dirs.filter((d) => d === 'inline').length,
        below: dirs.filter((d) => d === 'below').length,
        total: pairs.length,
    };
};

const scaleOrDefault = (t) => (t && typeof t.warn === 'number' ? t : GAP_THRESHOLDS);
const pctNum = (f) => Number((f * 100).toFixed(1));

/** The band labels for a gap scale: { over: '>105%', aligned: '95–105%', below: '<95%' }. */
export const alignmentLabels = (thresholds) => {
    const t = scaleOrDefault(thresholds);
    const hi = Number((100 + pctNum(t.warn)).toFixed(1));
    const lo = Number((100 - pctNum(t.warn)).toFixed(1));
    return { over: `>${hi}%`, aligned: `${lo}–${hi}%`, below: `<${lo}%` };
};

/**
 * Colour of the alignment average: red over 100 + critical (110 by
 * default), teal under 100 − warn (95), ok otherwise.
 */
export const alignmentTone = (avg, thresholds) => {
    const t = scaleOrDefault(thresholds);
    if (avg > 100 + pctNum(t.critical)) return 'crit';
    if (avg < 100 - pctNum(t.warn)) return 'below';
    return 'ok';
};

/**
 * AI sell speed by category prefix (see sellSpeedCategory). `total` is EVERY selected unit
 * (the % denominator today); `predicted` those with a category. Null when no
 * unit has a prediction.
 */
export const sellSpeedBreakdown = (units) => {
    if (!units?.length) return null;
    const starts = (prefix) => units.filter((u) => sellSpeedCategory(u)?.startsWith(prefix)).length;
    const fast = starts('Fast');
    const normal = starts('Normal');
    const verySlow = starts('Very Slow');
    const slow = starts('Slow');
    const predicted = fast + normal + slow + verySlow;
    if (predicted === 0) return null;
    return { fast, normal, slow, verySlow, total: units.length, predicted };
};

/** Whole-number share as today ("0" when the total is 0). */
export const pctOf = (count, total) => (total > 0 ? ((count / total) * 100).toFixed(0) : '0');

// ── Comparison flags (D15) ───────────────────────────────────────────────

/**
 * Outlier thresholds, in percentage points above the dealership share. The
 * asking vs AI flags have no numbers here on purpose: they use the shared
 * gap scale (GAP_THRESHOLDS via gapDirection in utils/lotValue.js).
 */
export const BRANCH_OUTLIER_THRESHOLDS = Object.freeze({
    aged: Object.freeze({ warn: 10, crit: 20 }),
    slow: Object.freeze({ warn: 10, crit: 25 }),
});

const overBy = (value, ref, { warn, crit }) => {
    const d = value - ref;
    if (d >= crit) return 'crit';
    if (d >= warn) return 'warn';
    return 'ok';
};

/** Share past 180 days against the dealership's. */
export const agedFlag = (agedPct, dealerAgedPct) => overBy(agedPct, dealerAgedPct, BRANCH_OUTLIER_THRESHOLDS.aged);
/** High sell-speed risk share against the dealership's. */
export const slowFlag = (riskPct, dealerRiskPct) => overBy(riskPct, dealerRiskPct, BRANCH_OUTLIER_THRESHOLDS.slow);

const GAP_FLAGS = { wellAbove: 'crit', above: 'warn', inline: 'ok', below: 'below' };
/**
 * Asking vs AI on the shared scale (the dealership's, default crit > +10%,
 * warn > +5%, below < −5%), ok, none.
 */
export const gapFlag = (gapPct, thresholds) => GAP_FLAGS[gapDirection(gapPct, thresholds)] || 'none';

/** "Stand out": a red aged or slow flag, Over AI (red) or Under AI. */
export const isStandOut = (flags) => flags.aged === 'crit' || flags.slow === 'crit'
    || flags.gap === 'crit' || flags.gap === 'below';

/** Tags shown next to a stand-out branch's name. */
export const outlierTags = (flags) => [
    flags.aged === 'crit' && { id: 'aged', label: 'Aged', tone: 'crit' },
    flags.slow === 'crit' && { id: 'slow', label: 'Slow sellers', tone: 'crit' },
    flags.gap === 'crit' && { id: 'over', label: 'Over AI', tone: 'crit' },
    flags.gap === 'below' && { id: 'under', label: 'Under AI', tone: 'below' },
].filter(Boolean);

/** Used units (AI prices exist for used stock only). */
const isUsedFlag = (value) => {
    const status = String(value ?? '').trim().toUpperCase();
    return status === 'U' || status === 'USED';
};
export const isUsedUnit = (unit) => isUsedFlag(unit?.NewUsedStatus) || isUsedFlag(unit?.TYPE);

/**
 * One row per /combined branch (API order) with the comparison metrics, the
 * flags against the dealership and the raw payload row; plus the dealership
 * reference. The AI gap comes from the inventory's used units, joined by
 * BranchName; a branch without priced used units has gapPct null (R13).
 */
export const buildBranchRows = (branches, units, { gapThresholds } = {}) => {
    const list = branches || [];
    const usedUnits = (units || []).filter(isUsedUnit);
    const lot = summarizeByBranch(usedUnits);
    const lotBy = new Map(lot.rows.map((r) => [r.branch, r]));
    const allNames = new Set(list.map((b) => b.branch_name));
    const deal = aggregateBranches(list, allNames);

    const dealership = deal ? {
        name: 'Dealership',
        units: deal.total,
        totalValue: deal.total_value,
        agedPct: deal.total > 0 ? (deal.critical / deal.total) * 100 : 0,
        riskPct: deal.pct_high_risk,
        activePct: deal.active_pct,
        avgDays: deal.avg_days_in_stock,
        gapPct: lot.total.pricedCount > 0 ? lot.total.gapPct : null,
        gapTotal: lot.total.gapTotal,
    } : null;

    const rows = list.map((b) => {
        const l = lotBy.get(b.branch_name);
        const gapPct = l && l.pricedCount > 0 ? l.gapPct : null;
        const row = {
            name: b.branch_name,
            units: b.total,
            totalValue: b.total_value ?? null,
            agedPct: b.total > 0 ? (b.critical / b.total) * 100 : 0,
            riskPct: b.pct_high_risk ?? 0,
            activePct: b.active_pct ?? 0,
            avgDays: b.avg_days_in_stock ?? null,
            gapPct,
            gapTotal: l && l.pricedCount > 0 ? l.gapTotal : null,
            branch: b,
        };
        row.flags = {
            aged: dealership ? agedFlag(row.agedPct, dealership.agedPct) : 'ok',
            slow: dealership ? slowFlag(row.riskPct, dealership.riskPct) : 'ok',
            gap: gapFlag(gapPct, gapThresholds),
        };
        row.standOut = isStandOut(row.flags);
        return row;
    });

    return { rows, dealership };
};

/** Comparator with null / undefined last in both directions. */
export const compareNullsLast = (dir) => (a, b) => {
    const an = a === null || a === undefined;
    const bn = b === null || b === undefined;
    if (an || bn) return an === bn ? 0 : an ? 1 : -1;
    if (typeof a === 'string') return dir === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
    return dir === 'asc' ? a - b : b - a;
};

/** Stable sort of comparison rows by one key. */
export const sortBranchRows = (rows, sort) => {
    if (!sort?.key) return rows;
    const cmp = compareNullsLast(sort.dir);
    return rows
        .map((r, i) => [r, i])
        .sort(([a, ai], [b, bi]) => cmp(a[sort.key], b[sort.key]) || ai - bi)
        .map(([r]) => r);
};

/**
 * "+11.0 pts vs dealership" and its tone: crit / warn past the outlier
 * thresholds, good when as far below, neutral otherwise.
 */
export const deltaVsDealership = (value, ref, { warn, crit }, label = 'vs dealership') => {
    // From the values at one decimal, as the compare panel shows them, so the
    // band and the panel never disagree by a rounding step.
    const d = Number((Number(value.toFixed(1)) - Number(ref.toFixed(1))).toFixed(1));
    const tone = d >= crit ? 'crit' : d >= warn ? 'warn' : d <= -warn ? 'good' : 'neutral';
    return { text: `${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(1)} pts ${label}`, tone };
};

// ── Inventory links (D14) ────────────────────────────────────────────────

// ?branch= value meaning "every branch" (defined with the inventory's parser).
export { ALL_BRANCHES_PARAM };

/**
 * The inventory URL for a selection and optional drill-down params
 * (aging, risk, interest, signal, sort). A partial selection goes in
 * ?branch=A,B. With every branch selected no ?branch is needed when a drill
 * param is there (it counts as an interaction, so saved prefs stay out); a
 * plain link says ?branch=all, or saved prefs could re-apply an old location.
 */
export const inventoryLink = ({ names = [], allNames = [], params = {} } = {}) => {
    const q = new URLSearchParams();
    const all = allNames.length > 0 && names.length === allNames.length;
    if (!all && names.length) q.set('branch', names.join(','));
    let hasDrill = false;
    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;
        q.set(key, value);
        hasDrill = true;
    });
    if (all && !hasDrill) q.set('branch', ALL_BRANCHES_PARAM);
    const search = q.toString();
    return search ? `/?${search}` : '/';
};

// ── Chart scales ─────────────────────────────────────────────────────────

const stepFor = (span) => {
    if (span <= 10) return 2;
    if (span <= 25) return 5;
    if (span <= 60) return 10;
    if (span <= 150) return 25;
    return 50;
};
const ticks = (from, to, step) => {
    const out = [];
    for (let v = from; v <= to + 1e-9; v += step) out.push(Math.round(v * 100) / 100);
    return out;
};

/**
 * Scatter axes that fit the data (QA BR-5): x (share past 180 days, %) and
 * y (gap %) each span the points and the dealership reference, padded by a
 * quarter step and rounded out to a nice step; x stays within 0–100%, y
 * always shows zero.
 */
export const scatterDomain = (points, ref) => {
    const xs = [...points.map((p) => p.x), ref?.x ?? 0];
    const xLo = Math.min(...xs);
    const xHi = Math.max(...xs);
    const xStep = stepFor(Math.max(xHi - xLo, 10));
    const xMin = Math.max(0, Math.floor((xLo - xStep * 0.25) / xStep) * xStep);
    const xMax = Math.min(100, Math.ceil((xHi + xStep * 0.25) / xStep) * xStep);
    const ys = [...points.map((p) => p.y), ref?.y ?? 0, 0];
    const yLo = Math.min(...ys);
    const yHi = Math.max(...ys);
    const yStep = stepFor(Math.max(yHi - yLo, 10));
    const yMin = Math.floor((yLo - yStep * 0.25) / yStep) * yStep;
    const yMax = Math.ceil((yHi + yStep * 0.25) / yStep) * yStep;
    return {
        xMin, xMax, yMin, yMax, xTicks: ticks(xMin, xMax, xStep), yTicks: ticks(yMin, yMax, yStep),
    };
};

/** Scale for the matrix share bars: largest value rounded up to the next 10. */
export const mixScale = (values) => Math.max(10, Math.ceil(Math.max(0, ...values) / 10) * 10);

// ── AI value by location (BranchAiValueTable, v1 and v2) ─────────────────

/**
 * Rows, totals and coverage for the AI value table. All selected (or no
 * selection given): every priced branch and the Dealership as the total.
 * Partial: the selected priced branches, a Selection total when two or more,
 * and the Dealership for reference.
 */
export const buildAiValueView = (units, selectedBranches, allSelected) => {
    const usedUnits = (Array.isArray(units) ? units : []).filter(isUsedUnit);
    const showAll = Boolean(allSelected || !selectedBranches);
    const summary = summarizeByBranch(usedUnits);
    const dealership = summary.total;
    const priced = summary.rows.filter((r) => r.pricedCount > 0);
    if (showAll) {
        return {
            usedUnits, showAll, rows: priced, selection: null, dealership,
            scopeUsed: usedUnits.length, scopePriced: dealership.pricedCount,
        };
    }
    const selected = selectedBranches instanceof Set ? selectedBranches : new Set(selectedBranches);
    const scopeUnits = usedUnits.filter((u) => selected.has(branchOf(u)));
    const selectionTotal = summarizeLotValue(scopeUnits);
    const rows = priced.filter((r) => selected.has(r.branch));
    return {
        usedUnits,
        showAll,
        rows,
        selection: rows.length > 1 ? selectionTotal : null,
        dealership,
        scopeUsed: scopeUnits.length,
        scopePriced: selectionTotal.pricedCount,
    };
};

/** One symmetric scale for every gap bar on screen (summary rows included). */
export const gapScaleOf = (rows) => rows.reduce((max, r) => (
    r && typeof r.gapPct === 'number' ? Math.max(max, Math.abs(r.gapPct)) : max
), 0);
